import { Center, HStack, Progress, Stack, Text } from '@chakra-ui/react';
import React from 'react';

import {
  date as commonDate,
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  DeliverableArtifactGQLType,
  DeliverableContainerGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { Icon } from 'packages/common-frontend/components/icons';

export type Status =
  | 'notStarted'
  | 'complete'
  | 'generatingReport'
  | 'overdue'
  | 'dueSoon';

export const statusColor: { [key in Status]: string } = {
  overdue: 'red',
  dueSoon: 'orange',
  notStarted: 'blue',
  complete: 'green',
  generatingReport: 'orange',
};

export const StatusIcon = ({ status }: { status: Status }) => {
  switch (status) {
    case 'complete':
      return <Icon icon="circle-check" color={`${statusColor[status]}.300`} />;
    case 'generatingReport':
      return <Icon icon="spinner" color={`${statusColor[status]}.300`} />;
    case 'dueSoon':
      return (
        <Icon icon="calendar-clock" color={`${statusColor[status]}.300`} />
      );
    case 'overdue':
      return (
        <Icon icon="calendar-clock" color={`${statusColor[status]}.300`} />
      );
    case 'notStarted':
      return (
        <Icon icon="calendar-clock" color={`${statusColor[status]}.300`} />
      );
    default:
      return <Icon icon="question" color={`${statusColor[status]}.300`} />;
  }
};

export const StatusCell = ({ status }: { status: Status }) => {
  return (
    <HStack alignItems="center">
      <Center paddingRight="5px">
        <StatusIcon status={status} />
      </Center>
      <Text>{commonUtils.toSentenceCase(status)}</Text>
    </HStack>
  );
};

export const calculateStatus = (
  container: DeliverableContainerGQLType,
): Status => {
  const currentDate = commonDate.currentDateStr();
  const diff = commonDate.diffDays(container.dueDate, currentDate);

  if (container.totalFinalArtifacts === 0) {
    return 'notStarted';
  }
  if (container.completedFinalArtifacts === container.totalFinalArtifacts) {
    return 'complete';
  }
  if (container.completedDependencies === container.totalDependencies) {
    return 'generatingReport';
  }
  if (diff < 0) {
    return 'overdue';
  }
  return 'dueSoon';
};

export type ArtifactStatus = 'submitted' | 'processing' | 'notYetSubmitted';

export const calculateArtifactStatus = (
  artifact: DeliverableArtifactGQLType,
): ArtifactStatus => {
  const { instances } = artifact;

  const activeInstance = instances.find((i) => i && i.active);

  if (activeInstance) {
    if (activeInstance.status === 'success') {
      return 'submitted';
    }
    if (activeInstance.status === 'inProgress') {
      return 'processing';
    }
    // if activeInstance.status === 'error' then artifact status should still be 'notYetSubmitted'
  }

  return 'notYetSubmitted';
};

export const artifactStatusColor: { [key in ArtifactStatus]: string } = {
  notYetSubmitted: 'orange',
  processing: 'orange',
  submitted: 'green',
};

export const UploadProgressBar = ({
  progressValue,
}: {
  progressValue: number;
}) => {
  return (
    <Stack width="max-content">
      <Text fontSize="sm" fontWeight="semibold">
        Upload in progress.
      </Text>
      <Progress
        value={progressValue}
        colorScheme="green"
        borderRadius={8}
        size="sm"
      />
    </Stack>
  );
};

export const UploadErrorCell = ({
  isTextColored = false,
}: {
  isTextColored: boolean;
}) => {
  const color = 'red.300';
  return (
    <HStack alignItems="center">
      <Center paddingRight="5px">
        <Icon icon="circle-x" color={color} />
      </Center>
      <Text color={isTextColored ? color : 'inherit'} fontWeight="semibold">
        Upload Error
      </Text>
    </HStack>
  );
};

export const ArtifactStatusCell = ({
  status,
  isTextColored = false,
}: {
  status: ArtifactStatus;
  isTextColored: boolean;
}) => {
  const color = `${artifactStatusColor[status]}.300`;
  switch (status) {
    case 'submitted':
      return (
        <HStack alignItems="center">
          <Center paddingRight="5px">
            <Icon icon="mailbox-flag-up" color={color} />
          </Center>
          <Text color={isTextColored ? color : 'inherit'}>
            {commonUtils.toSentenceCase(status)}
          </Text>
        </HStack>
      );
    case 'processing':
      return (
        <HStack alignItems="center">
          <Center paddingRight="5px">
            <Icon icon="spinner-third" color="orange.300" />
          </Center>
          <Text>{commonUtils.toSentenceCase(status)}</Text>
        </HStack>
      );
    case 'notYetSubmitted':
      return (
        <HStack alignItems="center">
          <Center paddingRight="5px">
            <Icon icon="mailbox" color={color} />
          </Center>
          <Text color={isTextColored ? color : 'inherit'}>
            {commonUtils.toSentenceCase(status)}
          </Text>
        </HStack>
      );
    default:
      return finleyAssertUnreachable(status);
  }
};
